import { useCallback, useState } from "react";

export type ProgressStage =
  | "idle"
  | "received"
  | "preparing"
  | "sending_to_llm"
  | "awaiting_llm"
  | "saving"
  | "saving_changes"
  | "recovering"
  | "done"
  | "error";

const STREAM_STAGE_NAMES = [
  "received",
  "preparing",
  "sending_to_llm",
  "awaiting_llm",
  "saving",
] as const;
type StreamStageName = (typeof STREAM_STAGE_NAMES)[number];

function isStreamStageName(value: string): value is StreamStageName {
  return (STREAM_STAGE_NAMES as readonly string[]).includes(value);
}

export interface ProgressState {
  stage: ProgressStage;
  label: string;
  percent: number;
}

export const STAGE_INFO: Record<ProgressStage, { label: string; percent: number }> = {
  idle: { label: "", percent: 0 },
  received: { label: "Preparing context…", percent: 8 },
  preparing: { label: "Preparing context…", percent: 22 },
  sending_to_llm: { label: "Sending text to the AI…", percent: 38 },
  awaiting_llm: { label: "Waiting for the AI response…", percent: 65 },
  saving: { label: "Saving section…", percent: 90 },
  saving_changes: { label: "Saving changes…", percent: 96 },
  recovering: { label: "Connection dropped, finishing on the server…", percent: 80 },
  done: { label: "Done", percent: 100 },
  error: { label: "Something went wrong", percent: 100 },
};

export function stageToState(stage: ProgressStage): ProgressState {
  const info = STAGE_INFO[stage];
  return { stage, label: info.label, percent: info.percent };
}

export type RunErrorKind = "network_error" | "http_error" | "stream_error" | "stream_drop";

export class RunError extends Error {
  kind: RunErrorKind;
  response?: Response;
  constructor(kind: RunErrorKind, message: string, response?: Response) {
    super(message);
    this.name = "RunError";
    this.kind = kind;
    this.response = response;
  }
}

interface RunOptions {
  url: string;
  body: unknown;
  signal?: AbortSignal;
  onStage?: (stage: ProgressStage) => void;
}

export interface RunResult {
  content: string;
  sourceMap?: unknown;
}

interface SsePayload {
  content?: unknown;
  sourceMap?: unknown;
  message?: unknown;
}

function parseSsePayload(text: string): SsePayload {
  if (!text) return {};
  try {
    const parsed = JSON.parse(text);
    return (parsed && typeof parsed === "object" ? (parsed as SsePayload) : {});
  } catch {
    return {};
  }
}

/**
 * Run a regenerate-section style request, preferring SSE for progress updates.
 * Falls back to plain JSON with a simulated time-based stage progression.
 */
export async function runWithProgress(opts: RunOptions): Promise<RunResult> {
  const { url, body, signal, onStage } = opts;
  const emit = (s: ProgressStage) => {
    try { onStage?.(s); } catch {}
  };

  emit("received");

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "text/event-stream, application/json",
      },
      credentials: "include",
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    emit("error");
    const message = err instanceof Error ? err.message : "Network request failed";
    throw new RunError("network_error", message);
  }

  if (!response.ok) {
    emit("error");
    throw new RunError("http_error", `Request failed with status ${response.status}`, response);
  }

  const ctype = response.headers.get("content-type") || "";
  if (!ctype.includes("text/event-stream") || !response.body) {
    // JSON fallback. Simulate progression briefly so the bar advances.
    emit("preparing");
    await new Promise((r) => setTimeout(r, 80));
    emit("sending_to_llm");
    await new Promise((r) => setTimeout(r, 80));
    emit("awaiting_llm");
    const data = (await response.json()) as SsePayload;
    emit("saving");
    await new Promise((r) => setTimeout(r, 60));
    // Caller handles the post-save Convex wait and will emit
    // "saving_changes" before clearing; do not emit "done" here.
    return {
      content: typeof data.content === "string" ? data.content : "",
      sourceMap: data.sourceMap,
    };
  }

  // Parse SSE stream.
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let finalResult: RunResult | null = null;
  let errorMessage: string | null = null;

  const handleEvent = (event: string, dataText: string) => {
    const data = parseSsePayload(dataText);
    if (event === "done") {
      finalResult = {
        content: typeof data.content === "string" ? data.content : "",
        sourceMap: data.sourceMap,
      };
      // Do not emit "done" here. The caller is still waiting for the
      // Convex live subscription to confirm the save and will emit
      // "saving_changes" before clearing the stage. Emitting "done"
      // here would briefly flash the bar to 100% and then regress to
      // 96% ("Saving changes…"), which is visually confusing.
    } else if (event === "error") {
      errorMessage = typeof data.message === "string" ? data.message : "Stream error";
      emit("error");
    } else if (isStreamStageName(event)) {
      emit(event);
    }
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sepIdx: number;
    while ((sepIdx = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, sepIdx);
      buffer = buffer.slice(sepIdx + 2);
      let event = "message";
      const dataLines: string[] = [];
      for (const line of raw.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) dataLines.push(line.slice(5).trim());
      }
      handleEvent(event, dataLines.join("\n"));
    }
  }

  if (errorMessage) {
    throw new RunError("stream_error", errorMessage);
  }
  if (!finalResult) {
    // Abrupt stream end without a `done` event — treat as a recoverable drop
    // so callers can fall back to Convex live sync instead of failing fast.
    emit("error");
    throw new RunError("stream_drop", "Stream ended without a done event");
  }
  return finalResult;
}

/**
 * React state container for tracking per-section progress. Keyed by sectionId.
 */
export function useSectionProgress() {
  const [stages, setStages] = useState<Record<string, ProgressStage>>({});

  const set = useCallback((sectionId: string, stage: ProgressStage) => {
    setStages((prev) => ({ ...prev, [sectionId]: stage }));
  }, []);

  const clear = useCallback((sectionId: string) => {
    setStages((prev) => {
      if (!(sectionId in prev)) return prev;
      const next = { ...prev };
      delete next[sectionId];
      return next;
    });
  }, []);

  const get = useCallback(
    (sectionId: string): ProgressStage | undefined => stages[sectionId],
    [stages],
  );

  return { stages, set, clear, get };
}
